import React, { useState } from 'react';
import { ChevronLeft, ChevronRight, X, Upload, Camera } from 'lucide-react';

interface GuidelinePanelProps {
  type: 'scan' | 'camera';
  isInCameraView?: boolean;
  onClose?: () => void;
}

const GuidelinePanel: React.FC<GuidelinePanelProps> = ({ type, isInCameraView = false, onClose }) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  // Guidelines for different types
  const scanUploadGuidelines = [
    { src: '/scans/image2.png', label: 'Digital Impression - Occlusal View', description: 'STL screenshot showing full arch from above' },
    { src: '/scans/image4.png', label: 'Digital Impression - Lateral View', description: 'Side view showing bite relationship in 3D' },
    { src: '/scans/image2.png', label: 'Analysis Software Screenshot', description: 'Export from your orthodontic analysis software' },
    { src: '/scans/image4.png', label: 'CBCT/X-ray View', description: 'Panoramic or cephalometric radiograph views' },
  ];

  const cameraPhotoGuidelines = [
    { src: '/cameras/3D4D17B8-61DF-4B17-B214-A4A54D326124.PNG', label: 'Upper Arch Photo', description: 'Hold mouth open wide, camera directly above upper teeth' },
    { src: '/cameras/3D4D17B8-61DF-4B17-B214-A4A54D326124.PNG', label: 'Lower Arch Photo', description: 'Hold mouth open wide, camera directly above lower teeth' },
    { src: '/cameras/AF63DD27-C13A-46F9-9BC5-2106C7D1D624.jpg', label: 'Right Lateral Photo', description: 'Use cheek retractor, capture from the right side' },
    { src: '/cameras/AF63DD27-C13A-46F9-9BC5-2106C7D1D624.jpg', label: 'Left Lateral Photo', description: 'Use cheek retractor, capture from the left side' },
  ];

  const currentGuidelines = type === 'scan' ? scanUploadGuidelines : cameraPhotoGuidelines;
  const currentGuideline = currentGuidelines[currentImageIndex];

  const handleNextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % currentGuidelines.length);
  };

  const handlePrevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + currentGuidelines.length) % currentGuidelines.length);
  };

  // Top position style for camera view
  if (isInCameraView) {
    return (
      <div className="bg-white rounded-lg shadow-lg p-4 mb-4">
        <div className="flex items-center justify-between mb-3">
          <h4 className="font-medium text-medical-800 flex items-center space-x-2">
            <Camera className="w-5 h-5 text-primary-600" />
            <span>Camera Photo Guidelines</span>
          </h4>
          {onClose && (
            <button
              onClick={onClose}
              className="p-1 hover:bg-medical-100 rounded transition-colors"
            >
              <X className="w-4 h-4 text-medical-600" />
            </button>
          )}
        </div>
        
        <div className="flex items-start space-x-4">
          <div className="relative">
            <img 
              src={currentGuideline.src} 
              alt={currentGuideline.label} 
              className="w-48 h-48 object-cover rounded-lg"
            />
            <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 text-white text-xs p-1 rounded-b-lg">
              {currentImageIndex + 1} / {currentGuidelines.length}
            </div>
          </div>
          
          <div className="flex-1">
            <p className="text-base font-medium text-medical-700">{currentGuideline.label}</p>
            <p className="text-sm text-medical-600 mt-1">{currentGuideline.description}</p>
            
            <div className="flex space-x-2 mt-3">
              <button
                onClick={handlePrevImage}
                className="p-1 bg-medical-100 hover:bg-medical-200 rounded transition-colors"
              >
                <ChevronLeft className="w-4 h-4 text-medical-700" />
              </button>
              <button
                onClick={handleNextImage}
                className="p-1 bg-medical-100 hover:bg-medical-200 rounded transition-colors"
              >
                <ChevronRight className="w-4 h-4 text-medical-700" />
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Side panel style for regular view
  const Icon = type === 'scan' ? Upload : Camera;
  const title = type === 'scan' ? 'Scan Upload Examples' : 'Camera Photo Examples';
  const bgColor = type === 'scan' ? 'bg-blue-50' : 'bg-green-50';
  const borderColor = type === 'scan' ? 'border-blue-200' : 'border-green-200';
  const iconColor = type === 'scan' ? 'text-blue-600' : 'text-green-600';

  return (
    <div className={`bg-white rounded-lg shadow p-6 ${bgColor} border-2 ${borderColor}`}>
      <div className="flex items-center justify-between mb-4">
        <h4 className="font-medium text-medical-800 flex items-center space-x-2">
          <Icon className={`w-5 h-5 ${iconColor}`} />
          <span>{title}</span>
        </h4>
      </div>
      
      <div className="space-y-4">
        <div className="relative">
          <img 
            src={currentGuideline.src} 
            alt={currentGuideline.label} 
            className="w-full h-48 object-cover rounded-lg border-2 border-white"
          />
          <div className="absolute top-2 right-2 bg-black bg-opacity-50 text-white text-xs px-2 py-1 rounded">
            {currentImageIndex + 1} / {currentGuidelines.length}
          </div>
        </div>
        
        <div className="bg-white rounded-lg p-3">
          <p className="text-sm font-medium text-medical-700">{currentGuideline.label}</p>
          <p className="text-xs text-medical-600 mt-1">{currentGuideline.description}</p>
        </div>
        
        <div className="flex justify-center space-x-2">
          <button
            onClick={handlePrevImage}
            className="p-2 bg-white hover:bg-medical-50 rounded-lg transition-colors border border-medical-200"
          >
            <ChevronLeft className="w-5 h-5 text-medical-700" />
          </button>
          
          <div className="flex items-center space-x-1">
            {currentGuidelines.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentImageIndex(index)}
                className={`w-2 h-2 rounded-full transition-colors ${
                  index === currentImageIndex ? iconColor.replace('text', 'bg') : 'bg-medical-300'
                }`}
              />
            ))}
          </div>
          
          <button
            onClick={handleNextImage}
            className="p-2 bg-white hover:bg-medical-50 rounded-lg transition-colors border border-medical-200"
          >
            <ChevronRight className="w-5 h-5 text-medical-700" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default GuidelinePanel;